/** Path math: smoothing, transforms, and lossless conversion to SVG / Canvas. */
import type { PathData, PathSegment, Point } from './scene';

export function distance(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Apply a point transform to every control/anchor point in a path. */
export function transformPath(path: PathData, fn: (pt: Point) => Point): PathData {
  return {
    segments: path.segments.map((s): PathSegment => {
      switch (s.t) {
        case 'M':
          return { t: 'M', p: fn(s.p) };
        case 'L':
          return { t: 'L', p: fn(s.p) };
        case 'Q':
          return { t: 'Q', c: fn(s.c), p: fn(s.p) };
        case 'C':
          return { t: 'C', c1: fn(s.c1), c2: fn(s.c2), p: fn(s.p) };
        case 'Z':
          return { t: 'Z' };
        default:
          throw new Error('unknown path segment');
      }
    }),
  };
}

function fmt(v: number): string {
  return (Math.round(v * 100) / 100).toString();
}

/** Serialise to an SVG path `d` string — the lossless export pivot. */
export function pathToSvgD(path: PathData): string {
  const out: string[] = [];
  for (const s of path.segments) {
    switch (s.t) {
      case 'M':
        out.push(`M${fmt(s.p.x)} ${fmt(s.p.y)}`);
        break;
      case 'L':
        out.push(`L${fmt(s.p.x)} ${fmt(s.p.y)}`);
        break;
      case 'Q':
        out.push(`Q${fmt(s.c.x)} ${fmt(s.c.y)} ${fmt(s.p.x)} ${fmt(s.p.y)}`);
        break;
      case 'C':
        out.push(`C${fmt(s.c1.x)} ${fmt(s.c1.y)} ${fmt(s.c2.x)} ${fmt(s.c2.y)} ${fmt(s.p.x)} ${fmt(s.p.y)}`);
        break;
      case 'Z':
        out.push('Z');
        break;
    }
  }
  return out.join(' ');
}

type AnyCtx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** Replay path segments into a canvas context (begins a fresh path). */
export function applyPathToCtx(path: PathData, ctx: AnyCtx): void {
  ctx.beginPath();
  for (const s of path.segments) {
    switch (s.t) {
      case 'M':
        ctx.moveTo(s.p.x, s.p.y);
        break;
      case 'L':
        ctx.lineTo(s.p.x, s.p.y);
        break;
      case 'Q':
        ctx.quadraticCurveTo(s.c.x, s.c.y, s.p.x, s.p.y);
        break;
      case 'C':
        ctx.bezierCurveTo(s.c1.x, s.c1.y, s.c2.x, s.c2.y, s.p.x, s.p.y);
        break;
      case 'Z':
        ctx.closePath();
        break;
    }
  }
}

/**
 * Quadratic midpoint smoothing — turns a sampled polyline into a smooth path.
 * Each sample becomes a quadratic control point; curve passes through midpoints.
 */
export function buildSmoothPath(points: Point[]): PathData {
  const segs: PathSegment[] = [];
  const n = points.length;
  if (n === 0) return { segments: segs };
  if (n === 1) {
    // A dot: a zero-length line renders as a circle under a round cap.
    segs.push({ t: 'M', p: { ...points[0] } });
    segs.push({ t: 'L', p: { ...points[0] } });
    return { segments: segs };
  }
  if (n === 2) {
    segs.push({ t: 'M', p: { ...points[0] } });
    segs.push({ t: 'L', p: { ...points[1] } });
    return { segments: segs };
  }
  segs.push({ t: 'M', p: { ...points[0] } });
  for (let i = 1; i < n - 1; i++) {
    const mid = { x: (points[i].x + points[i + 1].x) / 2, y: (points[i].y + points[i + 1].y) / 2 };
    segs.push({ t: 'Q', c: { ...points[i] }, p: mid });
  }
  segs.push({ t: 'L', p: { ...points[n - 1] } });
  return { segments: segs };
}

function edgesToOutline(left: Point[], right: Point[]): PathData {
  const n = left.length;
  const segs: PathSegment[] = [];
  if (n === 0) return { segments: segs };
  segs.push({ t: 'M', p: { ...left[0] } });
  for (let i = 1; i < n; i++) segs.push({ t: 'L', p: { ...left[i] } });
  for (let i = n - 1; i >= 0; i--) segs.push({ t: 'L', p: { ...right[i] } });
  segs.push({ t: 'Z' });
  return { segments: segs };
}

/** Approximate a circular arc (centre, radius, start angle, signed sweep) as cubic beziers. */
export function arcToCubics(cx: number, cy: number, r: number, a0: number, sweep: number): PathSegment[] {
  const segs: PathSegment[] = [];
  const steps = Math.max(1, Math.ceil(Math.abs(sweep) / (Math.PI / 2)));
  const delta = sweep / steps;
  const k = (4 / 3) * Math.tan(delta / 4);
  let a = a0;
  for (let i = 0; i < steps; i++) {
    const a1 = a + delta;
    const p0 = { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
    const p3 = { x: cx + r * Math.cos(a1), y: cy + r * Math.sin(a1) };
    const c1 = { x: p0.x - k * r * Math.sin(a), y: p0.y + k * r * Math.cos(a) };
    const c2 = { x: p3.x + k * r * Math.sin(a1), y: p3.y - k * r * Math.cos(a1) };
    segs.push({ t: 'C', c1, c2, p: p3 });
    a = a1;
  }
  return segs;
}

/** Build a closed fill outline from a centreline spine and per-point widths, with round caps. */
export function strokeOutline(spine: Point[], width: number[]): PathData {
  const n = spine.length;
  if (n === 0) return { segments: [] };
  if (n === 1) return circlePath(spine[0], Math.max(0.5, (width[0] ?? 1) / 2));
  const left: Point[] = [];
  const right: Point[] = [];
  for (let i = 0; i < n; i++) {
    const prev = spine[Math.max(0, i - 1)];
    const next = spine[Math.min(n - 1, i + 1)];
    let dx = next.x - prev.x;
    let dy = next.y - prev.y;
    const len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;
    const w = Math.max(0, (width[i] ?? 1) / 2);
    left.push({ x: spine[i].x - dy * w, y: spine[i].y + dx * w });
    right.push({ x: spine[i].x + dy * w, y: spine[i].y - dx * w });
  }

  const segs: PathSegment[] = [];
  segs.push({ t: 'M', p: { ...left[0] } });
  for (let i = 1; i < n; i++) segs.push({ t: 'L', p: { ...left[i] } });

  const rEnd = Math.max(0, (width[n - 1] ?? 1) / 2);
  if (rEnd >= 0.25) {
    const c = spine[n - 1];
    const a0 = Math.atan2(left[n - 1].y - c.y, left[n - 1].x - c.x);
    for (const s of arcToCubics(c.x, c.y, rEnd, a0, -Math.PI)) segs.push(s);
  } else {
    segs.push({ t: 'L', p: { ...right[n - 1] } });
  }

  for (let i = n - 2; i >= 0; i--) segs.push({ t: 'L', p: { ...right[i] } });

  const rStart = Math.max(0, (width[0] ?? 1) / 2);
  if (rStart >= 0.25) {
    const c = spine[0];
    const a0 = Math.atan2(right[0].y - c.y, right[0].x - c.x);
    for (const s of arcToCubics(c.x, c.y, rStart, a0, -Math.PI)) segs.push(s);
  } else {
    segs.push({ t: 'L', p: { ...left[0] } });
  }

  segs.push({ t: 'Z' });
  return { segments: segs };
}

/** Calligraphic ribbon: offset each point along a fixed nib direction. */
export function nibOutline(pts: Point[], dirX: number, dirY: number, len: number): PathData {
  const h = len / 2;
  const left = pts.map((p) => ({ x: p.x + dirX * h, y: p.y + dirY * h }));
  const right = pts.map((p) => ({ x: p.x - dirX * h, y: p.y - dirY * h }));
  return edgesToOutline(left, right);
}

/** A circle as four cubic beziers — for dots and round terminals. */
export function circlePath(c: Point, r: number): PathData {
  const k = 0.5522847498307936 * r;
  return {
    segments: [
      { t: 'M', p: { x: c.x + r, y: c.y } },
      { t: 'C', c1: { x: c.x + r, y: c.y + k }, c2: { x: c.x + k, y: c.y + r }, p: { x: c.x, y: c.y + r } },
      { t: 'C', c1: { x: c.x - k, y: c.y + r }, c2: { x: c.x - r, y: c.y + k }, p: { x: c.x - r, y: c.y } },
      { t: 'C', c1: { x: c.x - r, y: c.y - k }, c2: { x: c.x - k, y: c.y - r }, p: { x: c.x, y: c.y - r } },
      { t: 'C', c1: { x: c.x + k, y: c.y - r }, c2: { x: c.x + r, y: c.y - k }, p: { x: c.x + r, y: c.y } },
      { t: 'Z' },
    ],
  };
}

/** Extract anchor (end) points from a path. */
export function pathAnchors(path: PathData): Point[] {
  const out: Point[] = [];
  for (const s of path.segments) {
    if (s.t === 'M' || s.t === 'L' || s.t === 'Q' || s.t === 'C') out.push({ ...s.p });
  }
  return out;
}

/**
 * SVG elliptical arc → cubic beziers (W3C endpoint-to-centre algorithm).
 * Returns only the trailing 'C' segments (no leading move); the caller is
 * already at (x1,y1). Degenerate radii collapse to a straight line.
 */
export function ellipticalArcToCubics(
  x1: number,
  y1: number,
  rx: number,
  ry: number,
  phiDeg: number,
  largeArc: boolean,
  sweepFlag: boolean,
  x2: number,
  y2: number,
): PathSegment[] {
  if (x1 === x2 && y1 === y2) return [];
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  if (rx < 1e-9 || ry < 1e-9) return [{ t: 'L', p: { x: x2, y: y2 } }];

  const phi = (phiDeg * Math.PI) / 180;
  const cosP = Math.cos(phi);
  const sinP = Math.sin(phi);

  const dx = (x1 - x2) / 2;
  const dy = (y1 - y2) / 2;
  const x1p = cosP * dx + sinP * dy;
  const y1p = -sinP * dx + cosP * dy;

  let rx2 = rx * rx;
  let ry2 = ry * ry;
  const lambda = (x1p * x1p) / rx2 + (y1p * y1p) / ry2;
  if (lambda > 1) {
    const s = Math.sqrt(lambda);
    rx *= s;
    ry *= s;
    rx2 = rx * rx;
    ry2 = ry * ry;
  }

  const sign = largeArc !== sweepFlag ? 1 : -1;
  const num = rx2 * ry2 - rx2 * y1p * y1p - ry2 * x1p * x1p;
  const den = rx2 * y1p * y1p + ry2 * x1p * x1p;
  const co = sign * (den === 0 ? 0 : Math.sqrt(Math.max(0, num / den)));
  const cxp = (co * (rx * y1p)) / ry;
  const cyp = (co * -(ry * x1p)) / rx;

  const cx = cosP * cxp - sinP * cyp + (x1 + x2) / 2;
  const cy = sinP * cxp + cosP * cyp + (y1 + y2) / 2;

  const ux = (x1p - cxp) / rx;
  const uy = (y1p - cyp) / ry;
  const vx = (-x1p - cxp) / rx;
  const vy = (-y1p - cyp) / ry;
  const angleBetween = (axx: number, ayy: number, bxx: number, byy: number): number => {
    const dot = axx * bxx + ayy * byy;
    const len = Math.sqrt((axx * axx + ayy * ayy) * (bxx * bxx + byy * byy));
    let a = Math.acos(Math.max(-1, Math.min(1, len === 0 ? 1 : dot / len)));
    if (axx * byy - ayy * bxx < 0) a = -a;
    return a;
  };
  const theta1 = angleBetween(1, 0, ux, uy);
  let dtheta = angleBetween(ux, uy, vx, vy);
  if (!sweepFlag && dtheta > 0) dtheta -= 2 * Math.PI;
  else if (sweepFlag && dtheta < 0) dtheta += 2 * Math.PI;

  const segs: PathSegment[] = [];
  const steps = Math.max(1, Math.ceil(Math.abs(dtheta) / (Math.PI / 2)));
  const delta = dtheta / steps;
  const onEllipse = (t: number): Point => ({
    x: cx + rx * Math.cos(t) * cosP - ry * Math.sin(t) * sinP,
    y: cy + rx * Math.cos(t) * sinP + ry * Math.sin(t) * cosP,
  });
  const deriv = (t: number): Point => ({
    x: -rx * Math.sin(t) * cosP - ry * Math.cos(t) * sinP,
    y: -rx * Math.sin(t) * sinP + ry * Math.cos(t) * cosP,
  });
  let t = theta1;
  for (let i = 0; i < steps; i++) {
    const tn = t + delta;
    const alpha = (4 / 3) * Math.tan((tn - t) / 4);
    const p1 = onEllipse(t);
    const p2 = onEllipse(tn);
    const d1 = deriv(t);
    const d2 = deriv(tn);
    segs.push({
      t: 'C',
      c1: { x: p1.x + alpha * d1.x, y: p1.y + alpha * d1.y },
      c2: { x: p2.x - alpha * d2.x, y: p2.y - alpha * d2.y },
      p: { x: p2.x, y: p2.y },
    });
    t = tn;
  }
  return segs;
}

/** Loose bounding box over a path's anchor and control points. */
export function bbox(path: PathData): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const add = (p: Point): void => {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  };
  for (const s of path.segments) {
    if (s.t === 'M' || s.t === 'L') add(s.p);
    else if (s.t === 'Q') {
      add(s.c);
      add(s.p);
    } else if (s.t === 'C') {
      add(s.c1);
      add(s.c2);
      add(s.p);
    }
  }
  if (minX === Infinity) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

/** Point + tangent angle at a specific arc-length along a polyline. */
export function pointAtLength(pts: Point[], dist: number): { p: Point; angle: number } | null {
  if (pts.length === 0) return null;
  if (pts.length === 1) return { p: { ...pts[0] }, angle: 0 };
  if (dist <= 0) return { p: { ...pts[0] }, angle: Math.atan2(pts[1].y - pts[0].y, pts[1].x - pts[0].x) };
  let acc = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    const segLen = Math.hypot(b.x - a.x, b.y - a.y);
    if (segLen === 0) continue;
    if (acc + segLen >= dist) {
      const t = (dist - acc) / segLen;
      return { p: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }, angle: Math.atan2(b.y - a.y, b.x - a.x) };
    }
    acc += segLen;
  }
  const a = pts[pts.length - 2];
  const b = pts[pts.length - 1];
  return { p: { ...b }, angle: Math.atan2(b.y - a.y, b.x - a.x) };
}

/** Walk a polyline, emitting a sample with tangent angle every `step` px. */
export function resampleByArcLength(pts: Point[], step: number): { p: Point; angle: number }[] {
  const out: { p: Point; angle: number }[] = [];
  if (pts.length === 0 || step <= 0) return out;
  out.push({
    p: { ...pts[0] },
    angle: pts.length > 1 ? Math.atan2(pts[1].y - pts[0].y, pts[1].x - pts[0].x) : 0,
  });
  let acc = 0;
  let nextAt = step;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    const segLen = Math.hypot(b.x - a.x, b.y - a.y);
    if (segLen === 0) continue;
    const ang = Math.atan2(b.y - a.y, b.x - a.x);
    while (nextAt <= acc + segLen) {
      const t = (nextAt - acc) / segLen;
      out.push({ p: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }, angle: ang });
      nextAt += step;
    }
    acc += segLen;
  }
  return out;
}
